import React, { useState, useRef, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useTheme, HalfScreen, XDivider, YDivider } from 'irmlib/styles'
import PropTypes from 'prop-types'
import moment from 'moment'
import { MainRouteSubRoutePage } from '../../AppControl/RoutePage'
import { IconButton, TextField, InputAdornment } from '../../../irmlib/src/components/Controls'
import SearchIcon from '../../Icons/SearchIcon'
import Filter from './Filter'
import ReportsTable from './ReportsTable'
import CSVImportIcon from '../../../icons/svg/csv-import-icon'
import { verificationUser, totalNoOfBoxesForJob } from '../../../services/common/appConstants'
import { getYesterday, downloadCSV, enumerateDaysBetweenDates, getYear, getWeekNumber, getDayName, roundNumber } from '../../../irmlib/src/common'
import { getUser } from '../../../redux/global'
import { clearBusy, setBusy, errorToast } from '../../../irmlib/src/containers'
import { getJobMetricsWithFilter, getSessionTime } from '../../../services/firestore/gdeMetrics'
import { getKph } from '../../../services/reports/jobStatistic'
import { getTimeSheets } from '../../../services/firestore/timeSheets'
import { SearchContainer, SearchTextContainer } from '../../../styles'
import DisplayAppliedFilters from './DisplayAppliedFilters'
import { defaultDateFormat, invalidDate } from '../../../common/constants'
import { useTranslation } from 'react-i18next'

const TitleSection = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Title = styled.h1`
  font-weight: ${props => props.theme.typography.fontWeightBold};
  font-size: 18px;
`

const SearchAndFilter = styled.div`
  display:flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  flex-grow: 1;
`
const Wrapper = styled.div`
  width: 48%;
  display: flex;

`
const VerticalDivider = () =>
  <YDivider height='8px' />

const cloneColumns = (columns = []) => {
  return JSON.parse(JSON.stringify(columns))
}

const Reports = (props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const tableRef = useRef(null)
  const userDetails = getUser()
  const deafaultEmployeeSelection = 'all-employees'
  const defaultFilters = {
    districtId: [
      userDetails.district
    ],
    country: userDetails.country,
    startDate: getYesterday(),
    endDate: getYesterday(),
    dataEntry: false,
    verification: false,
    allUsers: deafaultEmployeeSelection,
    accountLevel1: ''
  }
  const [appliedFilters, setFilters] = useState(defaultFilters)
  const [refresh, setTableRefresh] = useState(false)
  const [searchText, handleSearchText] = useState('')
  // eslint-disable-next-line no-unused-vars
  const [timeSheets, setTimeSheetsData] = useState([])
  const columns = useMemo(() => cloneColumns(props?.columns), [appliedFilters, searchText])
  const handleSearch = (searchText) => {
    handleSearchText(searchText)
  }

  const handleFilters = (filter) => {
    setFilters(filter)
    setTableRefresh((flag) => !flag)
  }
  
  // function for return final grand otal fields for the csv to be exported
  const getGrandTotal = (data) => {
    if (props?.isEmployeeLevel) {
      data[t('reports.employeeLevel.employee')] = t('reports.grandTotal')
      data[t('reports.employeeLevel.districtId')] = ''
      delete data[t('reports.employeeLevel.keyingTime')]
      delete data[t('reports.employeeLevel.paidTimeGdeTime')]
    } else {
      data[t('reports.customerLevel.districtId')] = ''
      data[t('reports.customerLevel.customerId')] = ''
      data[t('reports.customerLevel.jobNumber')] = t('reports.grandTotal')
    }
    return data
  }

  const handleExportCSV = () => {
    const date = moment().format('YYYY-MM-DD')
    let fileName
    let columns = []
    const dataManager = tableRef?.current?.dataManager
    const { columns: tableColumns = [], sortedData: data = [] } = dataManager
    const columnKeys = {}
    const calculateGrandTotal = {}

    if (props?.isEmployeeLevel) {
      fileName = `Employee_level_Report_${date}.csv`
      columns = [t('reports.employeeLevel.districtId'),
        t('reports.employeeLevel.employee'),
        t('reports.employeeLevel.paidTime'),
        t('reports.employeeLevel.gdeTime'),
        t('reports.employeeLevel.timeKeying'),
        t('reports.employeeLevel.dataEntryKeyStrokes'),
        t('reports.employeeLevel.verifyKeyStrokes'),
        t('reports.employeeLevel.totalKeyStrokes'),
        t('reports.employeeLevel.boxes'),
        t('reports.employeeLevel.files'),
        t('reports.employeeLevel.kphGde'),
        t('reports.employeeLevel.kphTime'),
        t('reports.employeeLevel.keyingTime'),
        t('reports.employeeLevel.paidTimeGdeTime')]
      tableColumns.unshift({
        title: t('reports.employeeLevel.districtId'),
        title1: 'reports.employeeLevel.districtId',
        field: 'districtId',
        headerAlign: 'center',
        editable: 'never',
        hidden: true,
        tableData: tableColumns[0].tableData
      })
    } else {
      fileName = `Customer_Level_Report_${date}.csv`
      columns = [t('reports.customerLevel.lastModifiedDate'),
        t('reports.customerLevel.transmittedDate'),
        t('reports.customerLevel.districtId'),
        t('reports.customerLevel.customerId'),
        t('reports.customerLevel.jobNumber'),
        t('reports.customerLevel.totalKeying'),
        t('reports.customerLevel.totalVerifyTime'),
        t('reports.customerLevel.totalKeystroke'),
        t('reports.customerLevel.totalBoxes'),
        t('reports.customerLevel.totalFiles'),
        t('reports.customerLevel.kph'),
        t('reports.customerLevel.files'),
        t('reports.customerLevel.templateName'),
        t('reports.customerLevel.orderNumber')]
      tableColumns.unshift({
        title: t('reports.customerLevel.districtId'),
        title1: 'reports.customerLevel.districtId',
        field: 'districtId',
        headerAlign: 'center',
        editable: 'never',
        hidden: true,
        tableData: tableColumns[0].tableData
      })
    }
    
    tableColumns.forEach(col => {
      columnKeys[col.field] = col.title
    })
    const generatedData = []
    data.forEach(val => {
      const colunmData = {}
      // eslint-disable-next-line array-callback-return
      Object.keys(val).map(key => {
        if (key in columnKeys) {
          colunmData[columnKeys[key]] = val[key]
          if (columnKeys[key] in calculateGrandTotal) {
            calculateGrandTotal[columnKeys[key]] = roundNumber(calculateGrandTotal[columnKeys[key]] + val[key])
          } else {
            calculateGrandTotal[columnKeys[key]] = val[key]
          }
        }
      })
      generatedData.push(colunmData)
    })
    const grandTotal = getGrandTotal(calculateGrandTotal)
    generatedData.push(grandTotal)
    downloadCSV(fileName, columns, generatedData)
  }

  // function to get week number, year and day of the dates (array of dates)
  const getWeekNumberWithDays = (dates) => {
    const data = {}
    dates.forEach(date => {
      // get and concatinate year and week - i.e 202152 (2021 is year and 52 is week)
      const yearAndWeek = `${getYear(date)}${getWeekNumber(date)}`
      // get day of the date - i.e Monday
      const dayOfTheDate = getDayName(date)
      // if key already exist push day to the existing week
      if (yearAndWeek in data) { 
        data[yearAndWeek].push(dayOfTheDate)
      } else {
        // add new key and value will be day
        data[yearAndWeek] = [dayOfTheDate]
      }
    })
    return data
  }

  const getPaidTimeFilterFormat = () => {
    const { startDate, endDate } = { ...appliedFilters }
    const dateRange = []
    if (startDate === endDate) {
      dateRange.push(startDate)
    } else {
      // get each date and day - between the start date & end date
      const getBetweenDates = enumerateDaysBetweenDates(startDate, endDate)
      dateRange.push(...getBetweenDates)
    }
    /* Get the concatinated value of year and week number
     * Example 202152 - 2021 is year and 52 is week number
     * In Timesheets collection employee data is in this format 202152-1234567
     * 202152 is 52nd week data of year 2021 for the employee/worker - 1234567 is the worker/employee id
     * filterFormat will be {202152: ['Friday', 'Saturday']} - if date is 2021-12-24 & 2021-12-25
    */
    const filterFormat = getWeekNumberWithDays(dateRange)
    return filterFormat
  }
  
  const getTimeSheetsCollection = async (filters, workerList) => {
    const timeSheetsQuery = []
    Object.keys(filters).forEach(filter => {
      for (const workerId in workerList) {
        timeSheetsQuery.push(getTimeSheets(`${filter}-${workerList[workerId]}`))
      }
    })
    const sheets = await Promise.all(timeSheetsQuery)
    const data = {}
    sheets?.forEach(sheet => {
      data[sheet.id] = sheet.data()
    })
    setTimeSheetsData(data)
    return data
  }

  const getSessionTimeData = async () => {
    const sessionTime = await getSessionTime(appliedFilters)
    const sessionData = {}
    sessionTime.forEach(session => {
      const { workerId, loggedTime = 0 } = session.data()
      if (workerId in sessionData) {
        sessionData[workerId] = roundNumber(loggedTime + sessionData[workerId])
      } else {
        sessionData[workerId] = loggedTime
      }
    })
    return sessionData
  }
  
  /**
   * Function to calculate total paid time for selected date range (between start and end date - user selection in filters)
   *
   * @param {object} dayTotals - Recorded paid time details for the week
   * @param {Array} days - Selected days in a week (based on the date range)
   * @returns {number} total paid time
   */
  const calculatePaidTime = (dayTotals = {}, days = []) => {
    let paidTime = 0
    days.forEach(day => {
      if (day in dayTotals) {
        // calculate each day activity hours and return sum value
        const activityHours = parseInt(dayTotals[day]?.activityHours)
        paidTime = paidTime + activityHours
      }
    })
    return paidTime
  }

  const getData = async () => {
    const paidTimeFilters = getPaidTimeFilterFormat()
    const cumulativeData = {}
    try {
      setBusy()
      let timeSheetsData = {}
      let sessionTimeData = {}
      const metricsData = await getJobMetricsWithFilter(appliedFilters, props?.isEmployeeLevel)
      if (props?.isEmployeeLevel) {
        const workersList = []
        metricsData.forEach(doc => {
          const worker = doc.data()?.workerId
          if (!workersList.includes(worker)) {
            workersList.push(worker)
          }
        })
       
        timeSheetsData = await getTimeSheetsCollection(paidTimeFilters, workersList)
        sessionTimeData = await getSessionTimeData()
      }
     
      metricsData.forEach((doc) => {
        const data = doc.data()

        if (!data?.isDeleted || data?.isDeleted === undefined) {
          // consider keyed time, keystrokes, boxes and files details only on data entry category
          data.totalVerifyTime = 0
          data.verificationKeyStrokes = 0
          data.dataEntryKeyStrokes = 0
          if (data?.jobCategory === verificationUser) {
            data.totalVerifyTime = data?.keyedTime
            data.verificationKeyStrokes = data?.keyStrokeCount
            // resetting values- bcz we are not using the verification keyedtime, keystrokes, boxes and file details
            data.totalRecordCount = 0
            data.totalBoxCount = 0
            // Verification user key strokes and keyed time is also captured now by commenting down  the below code
            // data.keyStrokeCount = 0
            // data.keyedTime = 0
          } else {
            data.dataEntryKeyStrokes = data?.keyStrokeCount
          }
          const { workerId = '', jobId = '', customerId = '', totalRecordCount: totalFiles = 0, workerFirstName, workerLastName, accountLevel1 = '', accountLevel1Description = '' } = data
          
          let totalBoxes
          if ((doc.data().dataEntryType === 'Maintenance Box' || doc.data().dataEntryType === 'New Box') && parseInt(doc.data().totalBoxCount) === totalNoOfBoxesForJob) {
            totalBoxes = 0
          } else {
            totalBoxes = parseInt(doc.data().totalBoxCount)
          }

          const kphTime = getKph(data?.keyStrokeCount, data?.keyedTime)
          const filesByHour = getKph(totalFiles, data?.keyedTime)
          const workerName = workerFirstName ? workerLastName ? `${workerFirstName} ${workerLastName} -` : '' : ''
          const customerName = accountLevel1Description ? `-${accountLevel1Description}` : ''
          const orderNumber = data?.jobOrderNumber
          let lastModifiedDate = moment(data?.jobLastModifyTs?.seconds, 'X').format(defaultDateFormat)
          lastModifiedDate = lastModifiedDate === 'Invalid date' ? invalidDate : lastModifiedDate
          let transmittedDate = moment(data?.jobTransmittedTs?.seconds, 'X').format(defaultDateFormat)
          transmittedDate = transmittedDate === 'Invalid date' ? invalidDate : transmittedDate
          data.totalRecordCount = totalFiles || 0
          data.kphTime = kphTime
          data.filesByHour = filesByHour
          data.orderNumber = orderNumber
          data.lastModifiedDate = lastModifiedDate
          data.transmittedDate = transmittedDate
          data.workerId = `${workerName || ''}${workerId.replace(/\d(?=\d{3})/g, '*')}`
          // display customer id and name
          data.accountLevel1 = `${accountLevel1}${customerName}`
          data.totalBoxCount = totalBoxes
          let paidTime = 0

          // paid time logics - only for employee level reports
          if (props?.isEmployeeLevel) {
            // check generated paid time filters with time sheets data
            Object.keys(paidTimeFilters).forEach(filter => {
              // In Timesheets collection employee data available in this format 202152-1234567
              // 202152 is 52nd week data of year 2021 and 1234567 is the worker/employee id
              // So timeSheetId will be {year&week number}-{worker/emp id}
              const timeSheetId = `${filter}-${workerId}`
              // if selected date range data available in the timesheets collection then calculate paid time
              if (timeSheetId in timeSheetsData) {
                const paidTimeData = timeSheetsData[timeSheetId]?.dayTotals
                paidTime = calculatePaidTime(paidTimeData, paidTimeFilters[filter])
              }
            })
            data.paidTime = paidTime
          }

          if (props?.isEmployeeLevel) {
            if (workerId in cumulativeData) {
              const { totalRecordCount: filesCount, keyedTime, keyStrokeCount, dataEntryKeyStrokes, verificationKeyStrokes, totalBoxCount: boxCount } = { ...cumulativeData[workerId] }
              const newDataEntryData = { ...data }
              newDataEntryData.totalRecordCount = roundNumber(filesCount + newDataEntryData.totalRecordCount)
              newDataEntryData.keyedTime = roundNumber(keyedTime + newDataEntryData.keyedTime)
              newDataEntryData.filesByHour = roundNumber(getKph(newDataEntryData.totalRecordCount, newDataEntryData.keyedTime))
              newDataEntryData.keyStrokeCount = roundNumber(keyStrokeCount + newDataEntryData.keyStrokeCount)
              newDataEntryData.dataEntryKeyStrokes = roundNumber(dataEntryKeyStrokes + newDataEntryData.dataEntryKeyStrokes)
              newDataEntryData.verificationKeyStrokes = roundNumber(verificationKeyStrokes + newDataEntryData.verificationKeyStrokes)
              newDataEntryData.kphTime = roundNumber(getKph(newDataEntryData.keyStrokeCount, newDataEntryData.keyedTime))
              newDataEntryData.totalBoxCount = roundNumber(boxCount + newDataEntryData.totalBoxCount)
              cumulativeData[workerId] = newDataEntryData
            } else {
              cumulativeData[workerId] = data
            }
            // set session related data to the employee data
            if (workerId in sessionTimeData) {
              const gdeTime = sessionTimeData[workerId]
              const gdeKph = roundNumber(getKph(cumulativeData[workerId]?.keyStrokeCount, gdeTime))
              const calculateKeyedGDE = (cumulativeData[workerId].keyedTime / gdeTime) * 100
              const paidTimeGdeTime = (cumulativeData[workerId].paidTime / gdeTime) * 100
              cumulativeData[workerId].gdeTime = (gdeTime / 3600).toFixed(1)
              cumulativeData[workerId].kphGde = gdeKph
              cumulativeData[workerId].keyedTimeGDETime = calculateKeyedGDE === Infinity ? 0 : `${roundNumber(calculateKeyedGDE)}%`
              cumulativeData[workerId].paidTimeGdeTime = paidTimeGdeTime === Infinity ? 0 : `${roundNumber(paidTimeGdeTime)}%`
            } else {
              cumulativeData[workerId].gdeTime = 0
              cumulativeData[workerId].kphGde = 0
              cumulativeData[workerId].keyedTimeGDETime = '0%'
              cumulativeData[workerId].paidTimeGdeTime = '0%'
            }
          } else {
            if (customerId in cumulativeData) {
              if (jobId in cumulativeData[customerId]) {
                const { totalRecordCount: filesCount, keyStrokeCount, keyedTime, totalBoxCount = 0, totalVerifyTime } = { ...cumulativeData[customerId][jobId] }
                const newDataEntryData = { ...data }
                newDataEntryData.totalRecordCount = roundNumber(filesCount + newDataEntryData.totalRecordCount)
                newDataEntryData.keyStrokeCount = roundNumber(keyStrokeCount + newDataEntryData.keyStrokeCount)
                newDataEntryData.keyedTime = roundNumber(keyedTime + newDataEntryData.keyedTime)
                newDataEntryData.totalBoxCount = roundNumber(totalBoxCount + newDataEntryData.totalBoxCount)
                newDataEntryData.filesByHour = roundNumber(getKph(newDataEntryData.totalRecordCount, newDataEntryData.keyedTime))
                newDataEntryData.kphTime = roundNumber(getKph(newDataEntryData.keyStrokeCount, newDataEntryData.keyedTime))
                newDataEntryData.totalVerifyTime = roundNumber(newDataEntryData.totalVerifyTime + totalVerifyTime)
                cumulativeData[customerId][jobId] = newDataEntryData
              } else {
                cumulativeData[customerId][jobId] = data
              }
            } else {
              cumulativeData[customerId] = { [jobId]: data }
            }
          }
        }
      })
      clearBusy()
    } catch (error) {
      errorToast({ message: error?.message, title: t('toast.failedToFetch') })
      clearBusy()
    }
    if (props.isEmployeeLevel) {
      Object.values(cumulativeData).forEach(jobId => {
        jobId.keyedTime = (jobId.keyedTime / 3600).toFixed(1)
      })
      return Object.values(cumulativeData)
    } else {
      const customerReports = []
      Object.values(cumulativeData).forEach(jobId => {
        Object.values(jobId).forEach(job => {
          job.keyedTime = (job.keyedTime / 3600).toFixed(1)
          job.totalVerifyTime = (job.totalVerifyTime / 3600).toFixed(1)
          customerReports.push(job)
        })
      })
      return customerReports
    }
    
    // if (props.isEmployeeLevel) {
    //   return Object.values(cumulativeData)
    // } else {
    //   const customerReports = []
    //   Object.values(cumulativeData).forEach(jobId => {
    //     Object.values(jobId).forEach(job => {
    //       customerReports.push(job)
    //     })
    //   })
    //   return customerReports
    // }
  }

  // Table search
  useEffect(() => {
    tableRef?.current?.dataManager?.changeSearchText(searchText)
    tableRef?.current?.setState(tableRef?.current?.dataManager?.getRenderState())
  }, [searchText])

  return <>
    <MainRouteSubRoutePage
      mainTitle='reports.title'
      displayTitle={false}
    >
      <TitleSection>
        <Title theme={theme}>{t(props?.pageTitle)}</Title>
        <div>
          {t('forms.export')}
          <IconButton id={'reports_export'} onClick={handleExportCSV}><CSVImportIcon /></IconButton>
        </div>
      </TitleSection>
      <SearchAndFilter>
        <HalfScreen />
        <Wrapper>
          <SearchContainer>
            <SearchTextContainer>
              <VerticalDivider />
              <TextField
                id={'Reports_search'} 
                onChange={(val) => handleSearch(val)}
                endAdornment={
                  <InputAdornment position='end'>
                    <SearchIcon />
                  </InputAdornment>
                }
              />
            </SearchTextContainer>
            <Filter handleFilters={handleFilters} isEmployeeLevel={props?.isEmployeeLevel} appliedFilters={appliedFilters} />
            <XDivider width='20px' />
          </SearchContainer>
        </Wrapper>
      </SearchAndFilter>
      <DisplayAppliedFilters appliedFilters={appliedFilters} />
      <ReportsTable 
        getData={getData}
        tableRef={tableRef}
        columns={columns}
        refresh={refresh}
        reports={{
          status: true, 
          isEmployeeLevel: props?.isEmployeeLevel 
        }}
        reportFor={props?.isEmployeeLevel ? 'EmployeeLevel' : 'CustomerLevel'}
      />

    </MainRouteSubRoutePage>
  </>
}
export default Reports

Reports.propTypes = {
  columns: PropTypes.array,
  pageTitle: PropTypes.string,
  isEmployeeLevel: PropTypes.bool
}

Reports.defaultProps = {
  columns: [],
  pageTitle: ''
}